import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject, type ReactNode } from "react"
import { TimelinePlayer, type TimelinePlayerPlaybackApi } from "@/components/timeline-player"
import {
  buildSingleVideoDocument,
  type EngineSources,
  type TimelineDocument,
  type TimelineSource,
} from "@/lib/timeline"
import { DEFAULT_VIDEO_OUTPUT_FPS } from "@/lib/video-output-fps"

export type SketchVideoCorrectionConfig = {
  streamUrl: string
  /**
   * Same-origin proxy for canvas frame capture (markups). streamUrl may 302→S3
   * and taint canvas; captureUrl avoids that.
   */
  captureUrl?: string | null
  sourceVersionId: number
  sourceInMs: number
  sourceOutMs: number
  markupMax: number
  correctionTrackId: string
  correctionClipId: string
  timelineStartMs: number
  shotId: number
  onQueued: () => void
  /** Enqueue failed after optimistic close — clear pending clip lock. */
  onQueueFailed?: () => void
  /** Full source duration (filmstrip / seek metadata); ruler uses trim window. */
  durationMs?: number | null
  frameRate?: number | null
  aspectRatio?: number | null
  /** Filmstrip thumbs for the bottom timeline track. */
  timelineFrameUrls?: string[]
}

type Props = {
  open: boolean
  config: SketchVideoCorrectionConfig
  /** Transparent SketchCanvas (or null while playing). */
  sketchOverlay: ReactNode
  /** Corner CTA (Save markup) — top-right of shell. */
  shellFloatingAction?: ReactNode
  onPlayingChange: (playing: boolean) => void
  /** Playback API ready (pause + media available). */
  onMediaReady: () => void
  /** Playhead settled after scrub/seek/pause — restore saved per-frame ink. */
  onPlayheadSettled: (ms: number) => void
  /** Scrub or play started — hide live ink immediately. */
  onPlayheadInteractStart?: () => void
  overlayRef: React.RefObject<HTMLDivElement | null>
  playbackApiRef: MutableRefObject<TimelinePlayerPlaybackApi | null>
}

/**
 * Video stage for SketchDialog videoCorrection mode:
 * TimelinePlayer + filmstrip + transparent sketch overlay over live video.
 */
export function SketchVideoCorrectionStage({
  open,
  config,
  sketchOverlay,
  shellFloatingAction = null,
  onPlayingChange,
  onMediaReady,
  onPlayheadSettled,
  onPlayheadInteractStart,
  overlayRef,
  playbackApiRef,
}: Props) {
  const [playing, setPlaying] = useState(false)
  const scrubbingRef = useRef(false)
  const settledMsRef = useRef<number | null>(null)
  const onPlayheadSettledRef = useRef(onPlayheadSettled)
  onPlayheadSettledRef.current = onPlayheadSettled
  const onPlayheadInteractStartRef = useRef(onPlayheadInteractStart)
  onPlayheadInteractStartRef.current = onPlayheadInteractStart

  const windowMs = Math.max(0, config.sourceOutMs - config.sourceInMs)

  const doc = useMemo<TimelineDocument>(
    () =>
      buildSingleVideoDocument({
        sourceId: config.sourceVersionId,
        durationMs: windowMs > 0 ? config.sourceOutMs : config.durationMs,
        sourceInMs: config.sourceInMs,
        sourceOutMs: config.sourceOutMs,
      }),
    [config.sourceVersionId, config.durationMs, config.sourceInMs, config.sourceOutMs, windowMs],
  )

  const engineSources = useMemo<EngineSources>(() => {
    const captureUrl = config.captureUrl?.trim()
    return {
      versionUrls: { [config.sourceVersionId]: config.streamUrl },
      assetUrls: {},
      ...(captureUrl ? { captureUrls: { [config.sourceVersionId]: captureUrl } } : {}),
    }
  }, [config.sourceVersionId, config.streamUrl, config.captureUrl])

  const timelineSources = useMemo<Record<string, TimelineSource>>(
    () => ({
      [String(config.sourceVersionId)]: {
        version_id: config.sourceVersionId,
        stream_url: config.streamUrl,
        duration_ms: config.durationMs ?? config.sourceOutMs,
        frame_rate: config.frameRate ?? DEFAULT_VIDEO_OUTPUT_FPS,
        aspect_ratio: config.aspectRatio ?? null,
        timeline_frame_urls: config.timelineFrameUrls ?? [],
      },
    }),
    [
      config.sourceVersionId,
      config.streamUrl,
      config.durationMs,
      config.sourceOutMs,
      config.frameRate,
      config.aspectRatio,
      config.timelineFrameUrls,
    ],
  )

  const notifyPlayheadSettled = useCallback((ms: number) => {
    const prev = settledMsRef.current
    settledMsRef.current = ms
    if (prev != null && Math.abs(prev - ms) < 1) return
    onPlayheadSettledRef.current(ms)
  }, [])

  const onPlaybackReady = useCallback(
    (api: TimelinePlayerPlaybackApi | null) => {
      playbackApiRef.current = api
      if (!api || !open) return
      api.controls.pause()
      setPlaying(false)
      onPlayingChange(false)
      settledMsRef.current = api.controls.currentMs()
      onMediaReady()
      onPlayheadSettledRef.current(settledMsRef.current)
    },
    [open, onMediaReady, onPlayingChange, playbackApiRef],
  )

  useEffect(() => {
    if (!open) {
      setPlaying(false)
      onPlayingChange(false)
      scrubbingRef.current = false
      settledMsRef.current = null
      return
    }
    const id = window.setInterval(() => {
      const api = playbackApiRef.current
      if (!api) return
      const nextPlaying = api.controls.isPlaying()
      const scrubbing = api.controls.isScrubbing()
      const ms = api.controls.currentMs()

      setPlaying((prev) => {
        if (prev === nextPlaying) return prev
        onPlayingChange(nextPlaying)
        if (!prev && nextPlaying) {
          // Play started — hide live ink immediately.
          onPlayheadInteractStartRef.current?.()
        }
        if (prev && !nextPlaying) {
          // Play → pause: restore ink for the pause frame.
          notifyPlayheadSettled(ms)
        }
        return nextPlaying
      })

      if (scrubbing) {
        if (!scrubbingRef.current) {
          scrubbingRef.current = true
          onPlayheadInteractStartRef.current?.()
        }
        return
      }
      if (scrubbingRef.current) {
        scrubbingRef.current = false
        if (!nextPlaying) notifyPlayheadSettled(ms)
      }
    }, 100)
    return () => window.clearInterval(id)
  }, [open, onPlayingChange, playbackApiRef, notifyPlayheadSettled])

  const showOverlay = !playing && sketchOverlay != null

  return (
    <div className="h-full min-h-0">
      <TimelinePlayer
        doc={doc}
        sources={engineSources}
        timelineSources={timelineSources}
        aspectRatio={config.aspectRatio ?? null}
        frameRate={config.frameRate ?? DEFAULT_VIDEO_OUTPUT_FPS}
        mediaDurationMs={windowMs > 0 ? windowMs : null}
        // Trim window is fixed from the shot timeline — no blade/resize/move in the modal.
        capabilities={{ blade: false, resize: false, deleteClips: false, moveClips: false }}
        showTimeline
        showTrackHeaders={false}
        fillHeight
        active={open}
        onPlaybackReady={onPlaybackReady}
        interactiveShellOverlay={
          showOverlay ? (
            <div ref={overlayRef} className="h-full w-full">
              {sketchOverlay}
            </div>
          ) : null
        }
        shellFloatingAction={shellFloatingAction}
      />
    </div>
  )
}
